<script setup>
import { computed, nextTick, watch } from 'vue';

import { useSeo } from '@/composables/useSeo';
import { useCatalogFilters } from '@/composables/useCatalogFilters';
import { useCatalogData } from '@/composables/useCatalogData';
import { useMediaQuery } from '@vueuse/core';
import CatalogFiltersDesktop from '@/components/catalog/CatalogFiltersDesktop.vue';
import CatalogFiltersMobile from '@/components/catalog/CatalogFiltersMobile.vue';
import CatalogPagination from '@/components/catalog/CatalogPagination.vue';
import CarCard from '@/components/cards/CarCard.vue';

import CatalogTags from '@/components/catalog/CatalogTags.vue';
import CatalogPriceTable from '@/components/catalog/CatalogPriceTable.vue';
const props = defineProps({
  data: {
    type: Object,
    required: true,
  },
  title: {
    type: String,
    default: () => '',
  },
  keywords: {
    type: String,
    default: () => '',
  },
  description: {
    type: String,
    default: () => '',
  },
  robots: {
    type: String,
    default: () => '',
  },
  image: {
    type: Object,
    default: () => ({}),
  },
  isStock: {
    type: Boolean,
    default: false,
  },
});
const isMobileOrTablet = useMediaQuery('(max-width: 1023px)');
const SORT_OPTIONS = [
  { value: 'novelty', label: $t('filters.sort_newer'), slug: null },
  {
    value: 'ascending',
    label: $t('filters.sort_ascending'),
    slug: 'sort-ascending',
  },
  {
    value: 'descending',
    label: $t('filters.sort_descending'),
    slug: 'sort-descending',
  },
  { value: 'abc', label: $t('filters.sort_abc'), slug: 'sort-abc' },
];
const catalogRef = ref(null);

const SCROLL_NEAR_THRESHOLD_PX = 180;
const STICKY_OFFSET_PX = 80;

const scrollToCatalogTopIfFar = () => {
  if (process.server) return;
  const el = catalogRef.value;
  if (!el) return;

  const rect = el.getBoundingClientRect();
  if (Math.abs(rect.top) <= SCROLL_NEAR_THRESHOLD_PX) return;

  const targetY = window.scrollY + rect.top - STICKY_OFFSET_PX;
  window.scrollTo({ top: Math.max(0, targetY), behavior: 'smooth' });
};
const type = computed(() => props.data.type);

const hasTransport = computed(() => type.value !== 'buses');

const {
  pageUrl,
  filters,
  draftFilters,
  apiUrl,
  draftApiUrl,
  activeFilterCount,
  activeFilterTags,
  setFilter,
  setTransport,
  setSort,
  setPage,
  removeFilterTag,
  clearFilters,
  cancelDraft,
  submitDraft,
  setDraftFilter,
  syncWithMeta,
  getFilterLink,
  hasAnyFilters,
} = useCatalogFilters(type.value);

const {
  requestData,
  meta,
  forFilters,
  allFilterValues,
  mobileForFilters,
  mobileAllFilterValues,
  loading,
  error,
  fetchMobilePreview,
  resetMobilePreview,
} = useCatalogData(apiUrl, draftApiUrl, `catalog-${type.value}`);

watch(
  requestData,
  () => {
    nextTick(() => {
      //scrollToCatalogTopIfFar()
    });
  },
  { deep: false },
);

// `forFilters.transport` is faceted by the currently applied filters (e.g.
// narrows to just "passenger" once a brand that only sells passenger cars
// is selected), but the tabs are meant to reflect the whole catalog type,
// not the current filter selection — they should only change when
// navigating to a different catalog root. `allFilterValues` carries the
// same shape unfiltered, so prefer it here (mirrors the existing
// forFilters-with-allFilterValues-fallback pattern used for price in
// CatalogFiltersDesktop.vue, just with the priority reversed).
const transportTabs = computed(
  () => allFilterValues.value?.transport || forFilters.value?.transport,
);

const transportModel = computed({
  get() {
    return filters.value.transport;
  },
  set(val) {
    setTransport(val);
  },
});
const sort = computed({
  get() {
    return filters.value.sort || 'novelty';
  },
  set(val) {
    setSort(val === 'novelty' ? null : val);
  },
});
const isFiltersUpdated = computed(() => !!meta.value);

// Синхронізація при першому завантаженні даних
watch(
  meta,
  (newMeta) => {
    if (newMeta?.filters) {
      syncWithMeta(newMeta.filters);
    }
  },
  { immediate: true },
);

const seoTitle = computed(() => {
  const baseTitle = meta.value?.seo_title || props.seo_title || props.title;
  return filters.value.page > 1
    ? `${baseTitle} — ${$t('filters.page')} ${filters.value.page}`
    : baseTitle;
});

const seoDescription = computed(() => {
  const baseDescription =
    meta.value?.meta_description || props.meta_description || props.intro;
  return filters.value.page > 1
    ? `${baseDescription} — ${$t('filters.page')} ${filters.value.page}`
    : baseDescription;
});
const catalogTitle = computed(() => {
  return meta.value?.h1 || '';
});

const catalogDescription = computed(() => {
  if (meta.value?.pagination?.current_page > 1) return '';
  return meta.value?.description || '';
});

const seoKeywords = computed(() => {
  return meta.value?.meta_keywords || props.meta_keywords || '';
});

const seoRobots = computed(() => {
  if (meta.value?.meta_robots) return meta.value.meta_robots;

  // 2. Noindex, якщо вибрано сортування (якщо sort не дефолтний)
  if (filters.value.sort && filters.value.sort !== 'novelty') {
    return 'noindex, nofollow';
  }

  // 3. Noindex, якщо вказано діапазон цін
  if (filters.value.price_from || filters.value.price_to) {
    return 'noindex, nofollow';
  }

  // 4. Логіка перетину фільтрів (SEO правило: не індексувати занадто вузькі вибірки)
  const filterKeys = [
    'brand',
    'bodytype',
    'transmission',
    'fuel',
    'wheel_drive',
    'model',
    'color',
    'region',
    'dealer',
  ];

  let activeGroupsCount = 0;
  let hasMultipleValuesInAnyGroup = false;

  filterKeys.forEach((key) => {
    const values = filters.value[key];
    if (Array.isArray(values) && values.length > 0) {
      activeGroupsCount++;
      if (values.length >= 2) {
        hasMultipleValuesInAnyGroup = true;
      }
    }
  });

  // Додаткова перевірка типу транспорту (якщо не легковий)
  if (filters.value.transport && filters.value.transport !== 'passenger') {
    activeGroupsCount++;
  }

  // Якщо вибрано 2+ значення в одній групі або задіяно 3+ різні групи фільтрів
  if (hasMultipleValuesInAnyGroup || activeGroupsCount >= 3) {
    return 'noindex, nofollow';
  }

  return props.robots || 'index, follow';
});
useSeo({
  title: seoTitle,
  description: seoDescription,
  keywords: seoKeywords,
  robots: seoRobots,
  image: props.image_cover?.src || null,
  path: computed(() => {
    const path = pageUrl.value;
    const searchParams = new URLSearchParams();
    if (filters.value.page > 1) {
      searchParams.set('page', filters.value.page);
    }

    const queryString = searchParams.toString();
    return queryString ? `${path}?${queryString}` : path;
  }),
  catalogTitle: catalogTitle,
  catalogDescription: catalogDescription,
});
function getFilterLabel(field, value) {
  if (field === 'price') {
    const from = filters.value.price_from;
    const to = filters.value.price_to;
    const labelFrom = from ? `${$t('filters.from_chosen')} ${from}` : '';
    const labelTo = to ? `${$t('filters.to_chosen')} ${to}` : '';
    return `${$t('filters.price_chosen')} ${labelFrom} ${labelTo} ${$t('filters.uah_chosen')}`.replace(
      /\s+/g,
      ' ',
    );
  }

  const options = allFilterValues.value?.[field];

  if (Array.isArray(options)) {
    const found = options.find(
      (opt) =>
        String(opt.slug) === String(value) || String(opt.id) === String(value),
    );
    if (found) return found.title;
  }

  return value;
}
const filterShowUrl = computed(() => {
  const f = filters.value;
  const filterKeys = [
    'brand',
    'bodytype',
    'transmission',
    'fuel',
    'wheel_drive',
    'model',
    'color',
    'region',
    'dealer',
  ];

  let activeGroupsCount = 0;
  filterKeys.forEach((key) => {
    if (f[key]?.length > 0) activeGroupsCount++;
  });

  if (f.transport && f.transport !== 'passenger') {
    activeGroupsCount++;
  }

  const result = {
    brand: true,
    bodytype: true,
    transmission: true,
    fuel: true,
    wheel_drive: true,
    transport: true,
    model: true,
    color: true,
    region: true,
    dealer: true,
  };

  filterKeys.forEach((key) => {
    if (activeGroupsCount >= 2 || f[key]?.length >= 1) {
      result[key] = false;
    }
  });

  if (activeGroupsCount >= 2 || (f.transport && f.transport !== 'passenger')) {
    result.transport = false;
  }

  return result;
});
</script>

<template>
  <section
    :class="[
      data?.background_color ? `bg-${data.background_color}` : '',
      data?.spacing?.top ? `section--top-${data.spacing.top}` : '',
      data?.spacing?.bottom ? `section--bottom-${data.spacing.bottom}` : '',
    ]"
    data-aos="fade-up"
    data-aos-easing="ease-out-cubic"
  >
    <div class="container">
      <div ref="catalogRef" class="catalog">
        <template 
          v-if="isFiltersUpdated || loading || error"
        >
          <Tabs
            v-if="hasTransport && transportTabs?.length > 1"
            v-model:value="transportModel"
            :scrollable="true"
            class="catalog__tabs"
          >
            <TabList
              :class="`catalog__tabs-list tabs-bg-${data.background_color}`"
            >
              <Tab
                v-for="t in transportTabs"
                :key="`catalog__tab${t.id}`"
                :value="t.slug"
                class="catalog__tab"
              >
                <CIcon
                  class="catalog__tab-icon"
                  :name="t.slug"
                  width="64"
                  height="64"
                />
                <NuxtImg
                  v-if="t?.image?.src"
                  :src="t?.image?.src"
                  :alt="t?.image?.alt"
                  :height="t?.image?.height"
                  :width="t?.image?.width"
                  class="catalog__tab-image"
                />
                <span class="catalog__tab-title h5 black" v-html="t.title"></span>
                <NuxtLink
                  v-if="filterShowUrl.transport"
                  :to="getFilterLink('transport', t.slug)"
                  class="catalog__tab-link"
                  aria-hidden="true"
                  tabindex="-1"
                  hidden
                >
                  {{ t.title }}
                </NuxtLink>
              </Tab>
            </TabList>
          </Tabs>
          <div
            :class="[
              `catalog__panel catalog__panel--${data.background_color} ${transportTabs?.length === 1 ? 'catalog__panel--mt0' : ''}`,
            ]"
          >
            <div class="catalog__panel-top">
              <CatalogFiltersMobile
                v-if="isMobileOrTablet && hasAnyFilters(forFilters)"
                :fetch-preview-fn="fetchMobilePreview"
                :reset-preview-fn="resetMobilePreview"
                :draft-api-url="draftApiUrl"
                :type="type"
                :draft-filters="draftFilters"
                :for-filters="mobileForFilters"
                :all-filter-values="mobileAllFilterValues"
                :active-filter-count="activeFilterCount"
                :has-transport="hasTransport"
                :filter-show-url="filterShowUrl"
                :get-link-fn="getFilterLink"
                @set-draft-filter="setDraftFilter"
                @cancel="cancelDraft"
                @submit="submitDraft"
                @clear="clearFilters"
              />
              <p
                v-if="meta?.pagination"
                class="mb-0 catalog__panel-founded body-3-regular"
              >
                {{ $t('filters.founded')
                }}<span class="catalog__panel-founded-value body-3-bold">
                  <template v-if="meta?.pagination?.total === 1">
                    {{ $t('filters.one_auto', meta?.pagination?.total) }}
                  </template>
                  <template
                    v-else-if="
                      meta?.pagination?.total > 1 && meta?.pagination?.total < 5
                    "
                  >
                    {{
                      $t('filters.few_auto', { count: meta?.pagination?.total })
                    }}
                  </template>
                  <template v-else>
                    {{
                      $t('filters.many_auto', { count: meta?.pagination?.total })
                    }}
                  </template>
                </span>
              </p>
              <div
                v-if="hasAnyFilters(forFilters)"
                class="catalog__panel-sorting"
              >
                <span class="catalog__panel-sorting-title body-3-regular">{{
                  $t('filters.sort')
                }}</span>
                <Select
                  v-model="sort"
                  :options="SORT_OPTIONS"
                  option-label="label"
                  option-value="value"
                  class="select-sorting catalog__panel-sorting-select"
                  size="small"
                  append-to="self"
                  overlay-class="select-sorting-overlay"
                >
                  <template #dropdownicon>
                    <CIcon
                      name="direction-small-black"
                      width="16"
                      height="16"
                      class="p-select-dropdown__icon"
                    />
                  </template>
                </Select>
              </div>
            </div>
            <div
              v-if="activeFilterTags && activeFilterTags.length > 0"
              class="catalog__panel-bottom"
            >
              <div class="catalog__selected">
                <ul class="m-0 p-0 catalog__selected-tags">
                  <template
                    v-for="tag in activeFilterTags"
                    :key="`${tag.field}-${tag.value}`"
                  >
                    <li class="catalog__selected-tag">
                      <span class="catalog__selected-tag-text link-normal">
                        {{ getFilterLabel(tag.field, tag.value) }}
                      </span>
                      <button
                        type="button"
                        class="catalog__selected-tag-remove"
                        :class="[`js-button-tag-${tag.value}`]"
                        :aria-label="`${$t('filters.delete_filter')} ${getFilterLabel(tag.field, tag.value)}`"
                        @click="removeFilterTag(tag.field, tag.value)"
                      >
                        <CIcon
                          name="close-white"
                          width="16"
                          height="16"
                          class="catalog__selected-tag-icon"
                        />
                      </button>
                    </li>
                  </template>
                </ul>
                <Button
                  text
                  size="small"
                  class="catalog__selected-clear link-normal js-button-catalog-clear"
                  @click="clearFilters"
                >
                  <CIcon
                    name="close-black"
                    width="16"
                    height="16"
                    class="catalog__selected-clear-icon"
                  />
                  <span>
                    {{ $t('filters.reset') }}
                  </span>
                </Button>
              </div>
            </div>
          </div>
          <div
            v-if="hasAnyFilters(forFilters)"
            :class="[
              `catalog__filters catalog__filters--${data.background_color}`,
              isMobileOrTablet ? 'catalog__filters--mobile' : '',
            ]"
          >
            <CatalogFiltersDesktop
              v-if="!isMobileOrTablet"
              :type="type"
              :filters="filters"
              :for-filters="forFilters"
              :all-filter-values="allFilterValues"
              :active-filter-tags="activeFilterTags"
              :has-transport="hasTransport"
              :filter-show-url="filterShowUrl"
              :get-link-fn="getFilterLink"
              @set-filter="setFilter"
              @remove-tag="removeFilterTag"
              @clear-filters="clearFilters"
            />
          </div>
          <div
            :class="[
              `catalog__content catalog__content--${data.background_color}`,
            ]"
          >
            <div
              v-if="error"
              class="catalog__state catalog__state--error body-2-regular text-center"
            >
              {{ $t('filters.error_catalog') }}
            </div>
            <template v-else>
              <div
                v-if="loading"
                :class="[
                  'catalog__overlay',
                  `catalog__overlay--${data?.background_color}`,
                ]"
                aria-hidden="true"
              >
                <div class="catalog__overlay-spinner"></div>
              </div>
              <div
                v-if="requestData.length === 0"
                class="catalog__state catalog__state--empty body-2-regular text-center"
              >
                {{ $t('filters.empty_catalog') }}
              </div>
              <div
                v-else
                :class="[`catalog__list catalog__list--${data.background_color}`]"
              >
                <div class="catalog__list-top">
                  <ul class="m-0 p-0 catalog__list-items">
                    <li
                      v-for="item in requestData"
                      :key="`catalog__list_item_${item.id}`"
                      class="catalog__list-item"
                    >
                      <CarCard
                        :url="item?.url"
                        :in-stock="item?.status"
                        :image="
                          item?.thumbnails || item?.image_cover || item?.brand
                        "
                        :empty-image="!item?.image_cover && !item?.thumbnails"
                        :name="item?.title"
                        :price="item?.pricing?.price_old"
                        :discount="item?.pricing?.discount"
                        :current-price="item?.pricing?.price_current"
                        :currency="item?.pricing?.currency"
                        :max-payment="item?.financing?.max_payment"
                        :monthly-payment="item?.financing?.monthly_payment"
                        :kasko="item?.financing?.kasko"
                        :tooltip="item?.financing?.tooltip"
                        :specs="item.status_car === 'stock' ? item?.specs : {}"
                        :is-stock-car="item.status_car === 'stock'"
                      />
                    </li>
                  </ul>
                </div>
                <CatalogPagination
                  v-if="meta?.pagination && meta.pagination.last_page > 1"
                  :current="meta.pagination.current_page"
                  :pagination="meta?.pagination"
                  @change="setPage"
                />
                <CatalogPriceTable
                  v-if="meta?.price_table && meta?.price_table?.items && meta?.price_table?.items.length > 0 && meta?.pagination?.current_page == 1"
                  :data="meta?.price_table"
                />
                <CatalogTags
                  v-if="meta?.tags && meta?.tags?.length > 0 && meta?.pagination?.current_page == 1"
                  :data="meta?.tags"
                />
              </div>
            </template>
          </div>
        </template>
        <template v-else>
          <div
            :class="[
              'catalog__overlay',
              `catalog__overlay--${data?.background_color}`,
            ]"
            aria-hidden="true"
          >
            <div class="catalog__overlay-spinner"></div>
          </div>
        </template>
      </div>
    </div>
  </section>
</template>

<style scoped lang="scss">
.catalog {
  $parent: &;
  display: grid;
  gap: 16px;
  grid-template-areas: 'tabs' 'panel' 'content';
  grid-template-columns: 1fr;
  position: relative;
  @include media-breakpoint-up(md) {
    align-items: start;
    grid-template-columns: 2.92fr 9.08fr;
    grid-template-areas: 'tabs tabs' 'panel panel' 'filters content';
  }
  @include media-breakpoint-up(xl) {
    @include responsive-spacing(gap, 16);
  }
  &__overlay {
    inset: 0;
    pointer-events: all;
    position: absolute;
    z-index: 5;
    &--white {
      background: rgba($white, 0.65);
    }
    &--gray {
      background: rgba($gray, 0.65);
    }
    &--black {
      background: rgba($black, 0.65);
    }
  }

  &__overlay-spinner {
    animation: catalog-spin 0.8s linear infinite;
    border: 3px solid $divider;
    border-radius: 50%;
    border-top-color: $black;
    height: 40px;
    margin: 0 auto;
    position: sticky;
    top: calc(50vh - 20px);
    width: 40px;
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(border-top-width, 3);
      @include responsive-spacing(height, 40);
      @include responsive-spacing(width, 40);
      top: calc(50vh - px-to-vw(20));
    }
  }
  &__content {
    grid-area: content;
    display: flex;
    flex-direction: column;
    gap: 16px;
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 16);
    }
    &--white {
      background-color: $gray;
    }
    &--black {
      background-color: rgba($surface-container-opacity, 0.5);
    }
    &--gray {
      background-color: $white;
    }
  }

  &__state {
    display: flex;
    flex-direction: column;
    gap: 16px;
    max-width: fit-content;
    margin: 0 auto;
    padding: 24px 0;
    text-align: center;
    @include media-breakpoint-up(md) {
      padding: 40px 0;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(gap, 16);
      @include responsive-spacing(padding-bottom, 40);
      @include responsive-spacing(padding-top, 40);
    }
  }

  &__tabs {
    grid-area: tabs;
  }
  &__tab {
    border-bottom: 1px solid $divider;
    cursor: pointer;
    display: flex;
    flex-shrink: 0;
    align-items: center;
    gap: 16px;
    padding: 0px 16px;
    position: relative;
    transition: color 0.2s ease;
    @include media-breakpoint-up(sm) {
      padding: 0 26px;
      min-width: 291px;
    }
    @include media-breakpoint-up(md) {
      padding: 0 38px;
      min-width: 296px;
      width: 25%;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(border-bottom-width, 1);
      @include responsive-spacing(gap, 16);
      @include responsive-spacing(min-width, 296);
      @include responsive-spacing(padding-left, 38);
      @include responsive-spacing(padding-right, 38);
    }
    &-link {
      display: none;
    }
  }
  &__panel {
    border-radius: 4px;
    display: flex;
    flex-direction: column;
    gap: 32px;
    grid-area: panel;
    padding: 16px;
    margin-top: 24px;
    @include media-breakpoint-up(md) {
      border-radius: 8px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(border-radius, 8);
      @include responsive-spacing(padding, 16);
      @include responsive-spacing(margin-top, 24);
      @include responsive-spacing(gap, 32);
    }
    &--white {
      background-color: $gray;
    }
    &--black {
      background-color: rgba($surface-container-opacity, 0.5);
    }
    &--gray {
      background-color: $white;
    }
    &--mt0 {
      margin-top: 0;
    }
    &-top {
      align-items: start;
      display: grid;
      grid-template-columns: auto auto;
      justify-content: space-between;
      gap: 24px;
      grid-template-areas: 'all-filters all-filters' 'founded sorting';
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(gap, 24);
      }
    }
    &-all {
      grid-area: all-filters;
      justify-content: space-between;
    }
    &-founded {
      color: $on-surface-variant-2;
      display: flex;
      flex-direction: column;
      gap: 8px;
      grid-area: founded;
      margin: 0;
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(gap, 8);
      }
      &-value {
        color: $black;
      }
    }
    &-sorting {
      display: flex;
      flex-direction: column;
      gap: 1px;
      grid-area: sorting;
      text-align: right;
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(gap, 1);
      }
      &-text {
        color: $on-surface-variant-2;
      }
    }
    &-bottom {
      padding-top: 16px;
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(padding-top, 16);
      }
    }
  }
  &__selected {
    border-top: 1px solid $divider;
    display: grid;
    grid-template-columns: 1fr;
    gap: 16px;
    padding-top: 16px;
    @include media-breakpoint-up(md) {
      grid-template-columns: auto auto;
      justify-content: space-between;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(border-top-width, 1);
      @include responsive-spacing(gap, 16);
      @include responsive-spacing(padding-top, 16);
    }
    &-tags {
      display: flex;
      flex-wrap: wrap;
      padding: 0;
      margin: 0;
      list-style: none;
      gap: 4px;
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(gap, 4);
      }
    }
    &-tag {
      align-items: center;
      background-color: $black;
      border-radius: 48px;
      color: $white;
      display: grid;
      grid-template-columns: max-content 16px;
      gap: 8px;
      margin: 0;
      min-height: 32px;
      padding: 7px 8px 7px 12px;
      transition: all 0.3s ease;
      &-text {
        max-width: 200px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(border-radius, 48);
        grid-template-columns: max-content px-to-vw(16);
        @include responsive-spacing(gap, 8);
        @include responsive-spacing(min-height, 32);
        @include responsive-spacing(padding-bottom, 7);
        @include responsive-spacing(padding-left, 12);
        @include responsive-spacing(padding-right, 8);
        @include responsive-spacing(padding-top, 7);
        &-text {
          @include responsive-spacing(max-width, 200);
        }
      }
    }
    &-clear {
      display: grid;
      grid-template-columns: 16px max-content;
      max-width: fit-content;
      @include media-breakpoint-up(xl) {
        grid-template-columns: px-to-vw(16) max-content;
      }
    }
  }
  &__filters {
    position: fixed;
    left: 0;
    grid-area: filters;
    top: 0;
    z-index: 3;
    display: none;
    @include media-breakpoint-up(md) {
      border-radius: 8px;
      display: block;
      margin-top: 0;
      position: relative;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(border-radius, 8);
    }
    &--mobile {
      /*display: block;
      position: fixed;
      inset: 0;
      z-index: 1001;
      max-width: 384px;*/
    }
    &--white {
      background-color: $gray;
    }
    &--black {
      background-color: rgba($surface-container-opacity, 0.5);
    }
    &--gray {
      background-color: $white;
    }
    &-header {
      align-items: center;
      background-color: $white;
      border-bottom: 1px solid $divider;
      border-radius: 4px 4px 0 0;
      display: grid;
      grid-template-columns: auto 32px;
      justify-content: space-between;
      padding: 18px 16px 15px 24px;
      position: sticky;
      top: 0;
      z-index: 2;
      &-button {
        align-items: center;
        display: flex;
        cursor: pointer;
        justify-content: center;
        height: 32px;
        width: 32px;
      }
      @include media-breakpoint-up(md) {
        display: none;
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(border-bottom-width, 1);
        @include responsive-spacing(border-top-left-radius, 4);
        @include responsive-spacing(border-top-right-radius, 4);
        grid-template-columns: auto px-to-vw(32);
        @include responsive-spacing(padding-bottom, 19);
        @include responsive-spacing(padding-top, 19);
        @include responsive-spacing(padding-left, 16);
        @include responsive-spacing(padding-right, 16);
        &-button {
          @include responsive-spacing(height, 32);
          @include responsive-spacing(width, 32);
        }
      }
      & + .catalog__filter-top {
        top: 65px;
        @include media-breakpoint-up(md) {
          border-radius: 8px 8px 0 0;
          border-top: unset;
          top: 0;
        }
      }
    }
    &-actions {
      background-color: $white;
      bottom: 0;
      padding: 32px 16px 96px;
      position: sticky;
      z-index: 2;
      &:before {
        background: linear-gradient(
          180deg,
          rgba(255, 255, 255, 0) 0%,
          #ffffff 95%
        );
        bottom: 144px;
        content: '';
        display: block;
        height: 72px;
        left: 0;
        position: absolute;
        width: 100%;
        z-index: 1;
      }
      &:after {
        background-color: $white;
        bottom: 0;
        content: '';
        display: block;
        height: 64px;
        left: 0;
        position: absolute;
        width: 100%;
        z-index: -1;
      }
    }
    &-button {
      width: 100%;
    }
  }
  &__filter {
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(border-top-width, 1);
      @include responsive-spacing(gap, 24);
      @include responsive-spacing(padding-bottom, 16);
      @include responsive-spacing(padding-top, 24);
      @include responsive-spacing(padding-left, 16);
      @include responsive-spacing(padding-right, 16);
    }
    &-top {
      background-color: $white;
      padding: 24px 16px 12px;
      position: sticky;
      top: 65px;
      z-index: 1;
      width: 100%;
      @include media-breakpoint-up(md) {
        top: 58px;
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(border-top-width, 1);
        @include responsive-spacing(padding-bottom, 12);
        @include responsive-spacing(padding-top, 24);
        @include responsive-spacing(padding-left, 16);
        @include responsive-spacing(padding-right, 16);
      }
    }
    &-header {
      align-items: center;
      cursor: pointer;
      display: grid;
      justify-content: space-between;
      grid-template-columns: auto 16px;
      padding: 0 8px;
      @include media-breakpoint-up(xl) {
        grid-template-columns: auto px-to-vw(16);
        @include responsive-spacing(padding-left, 8);
        @include responsive-spacing(padding-right, 8);
      }
    }
    &-title {
      color: $black;
    }
    &-link {
      display: none;
    }
    &-toggle {
      cursor: pointer;
      padding: 0;
      &-icon {
        height: auto;
        transform: rotate(90deg);
        transition: transform 0.3s ease;
        width: 100%;
        &--rotated {
          transform: rotate(-90deg);
        }
      }
    }
    &-group {
      display: flex;
      flex-direction: column;
      gap: 16px;
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(gap, 16);
      }
    }
    &-options {
      display: flex;
      flex-direction: column;
      gap: 4px;
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(gap, 4);
      }
    }
    &-option {
      display: flex;
      align-items: center;
      gap: 12px;
      min-height: 32px;
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(gap, 12);
        @include responsive-spacing(padding-bottom, 6);
        @include responsive-spacing(padding-top, 6);
        @include responsive-spacing(padding-left, 4);
        @include responsive-spacing(padding-right, 4);
        @include responsive-spacing(min-height, 32);
      }
    }
    &-checkbox {
      flex-shrink: 0;
    }
    &-show {
      &-button {
        align-items: center;
        display: grid;
        justify-content: start;
        gap: 8px;
        grid-template-columns: auto 16px;
        padding: 0 12px !important;
        @include media-breakpoint-up(md) {
          padding: 0 4px !important;
        }
        @include media-breakpoint-up(xl) {
          grid-template-columns: auto px-to-vw(16);
          @include responsive-spacing(gap, 8);
          @include responsive-spacing(padding-left, 4, true);
          @include responsive-spacing(padding-right, 4, true);
        }
      }
      &-arrow {
        height: auto;
        transform: rotate(90deg);
        width: 100%;
        &--up {
          transform: rotate(-90deg);
        }
      }
    }
    &-content {
      border-bottom: 1px solid $divider;
      padding: 12px 16px 16px;
      width: 100%;
      @include media-breakpoint-up(md) {
        &:last-child {
          border-bottom: unset;
        }
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(padding-bottom, 16);
        @include responsive-spacing(padding-top, 12);
        @include responsive-spacing(padding-left, 16);
        @include responsive-spacing(padding-right, 16);
      }
    }
    &-price {
      display: flex;
      flex-direction: column;
      gap: 32px;
      padding: 7px 8px 12px 8px;
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(gap, 32);
        @include responsive-spacing(padding-bottom, 12);
        @include responsive-spacing(padding-top, 7);
        @include responsive-spacing(padding-left, 8);
        @include responsive-spacing(padding-right, 8);
      }
      &-inputs {
        display: grid;
        grid-template-columns: 5fr 4px 5fr;
        gap: 8px;
        @include media-breakpoint-up(xl) {
          @include responsive-spacing(gap, 8);
          grid-template-columns: 5fr px-to-vw(4) 5fr;
        }
      }
      &-input {
        width: 100%;
      }
      &-divider {
        position: relative;
        &:before {
          background-color: $divider;
          bottom: 27px;
          content: '';
          position: absolute;
          height: 1px;
          width: 100%;
          @include media-breakpoint-up(xl) {
            @include responsive-spacing(bottom, 27);
            @include responsive-spacing(height, 1);
          }
        }
      }
      &-slider {
        width: 100%;
        &-container {
          padding: 0 8px;
          @include media-breakpoint-up(xl) {
            @include responsive-spacing(padding-left, 8);
            @include responsive-spacing(padding-right, 8);
          }
        }
      }
    }
  }
  &__list {
    border-radius: 4px;
    grid-area: items;
    margin: 0;
    padding: 0;
    @include media-breakpoint-up(md) {
      border-radius: 8px;
    }
    @include media-breakpoint-up(xl) {
      @include responsive-spacing(border-radius, 8);
    }
    &--white {
      background-color: $gray;
    }
    &--black {
      background-color: rgba($surface-container-opacity, 0.5);
    }
    &--gray {
      background-color: $white;
    }

    &-top {
      position: relative;
      padding: 0 0 44px 0;
      @include media-breakpoint-up(sm) {
        padding: 0 0 41px 0;
      }
      @include media-breakpoint-up(md) {
        padding: 0 0 60px 0;
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(padding-bottom, 60);
      }
    }

    &-bottom {
      border-top: 1px solid $divider;
      padding: 24px 16px;
      @include media-breakpoint-up(sm) {
        padding: 40px 48px;
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(padding-bottom, 40);
        @include responsive-spacing(padding-top, 40);
        @include responsive-spacing(padding-left, 48);
        @include responsive-spacing(padding-right, 48);
      }
    }

    &-items {
      align-items: start;
      display: grid;
      gap: 40px;
      list-style: none;
      padding: 0;
      margin: 0;
      @include media-breakpoint-up(sm) {
        column-gap: 16px;
        grid-template-columns: repeat(2, 1fr);
      }
      @include media-breakpoint-up(md) {
        row-gap: 60px;
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(column-gap, 16);
        @include responsive-spacing(row-gap, 60);
      }
    }

    &-item {
      height: 100%;
      margin: 0;
      padding-top: 40px;
      position: relative;
      &:before {
        background-color: $divider;
        content: '';
        height: 1px;
        left: 0;
        position: absolute;
        top: 0;
        right: 0;
        width: 100%;
      }

      &:first-child {
        &:before {
          display: none;
        }
      }

      @include media-breakpoint-up(sm) {
        &:before {
          width: calc(100% + 8px);
        }
        &:nth-child(2n + 2) {
          &:before {
            left: -8px;
          }
        }
        &:nth-child(2) {
          &:before {
            display: none;
          }
        }
      }

      @include media-breakpoint-up(md) {
        &:nth-child(2n + 1) {
          padding-left: 48px;
        }
        &:nth-child(2n + 2) {
          padding-right: 48px;
        }
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(padding-top, 40);
        &:before {
          @include responsive-spacing(height, 1);
          width: calc(100% + px-to-vw(8));
        }
        &:nth-child(2n + 1) {
          @include responsive-spacing(padding-left, 48);
        }
        &:nth-child(2n + 2) {
          @include responsive-spacing(padding-right, 48);
          &:before {
            @include responsive-spacing(left, -8);
          }
        }
      }

      &:deep(.car-item) {
        @include media-breakpoint-up(sm) {
          padding-bottom: 40px;
          padding-top: 41px;
        }
        @include media-breakpoint-up(md) {
          padding-bottom: 20px;
          padding-top: 41px;
        }
        @include media-breakpoint-up(xl) {
          @include responsive-spacing(padding-bottom, 20);
          @include responsive-spacing(padding-top, 41);
        }
      }
    }
    &-loader {
      border-radius: 4px;
      position: absolute;
      inset: 0;
      background: rgba($white, 0.9);
      display: flex;
      align-items: center;
      justify-content: center;
      pointer-events: none;
      @include media-breakpoint-up(md) {
        border-radius: 8px;
      }
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(border-radius, 8);
      }
    }
    &-spinner {
      width: 32px;
      height: 32px;
      border-radius: 50%;
      border: 3px solid $divider;
      border-top-color: $black;
      animation: catalog-spin 0.8s linear infinite;
      @include media-breakpoint-up(xl) {
        @include responsive-spacing(border-top-width, 3);
        @include responsive-spacing(height, 32);
        @include responsive-spacing(width, 32);
      }
    }
  }
  &__backdrop {
    background-color: rgba(0, 0, 0, 0.7);
    inset: 0;
    position: fixed;
    z-index: 1000;
  }
}

@keyframes catalog-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
